import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useIsPro } from 'hooks/useIsPro';
import { reloadWorkspaces, useWorkspace } from 'hooks/useWorkspace';
import cn from 'classnames';

import FilesService from 'services/files';
import { updateWorkspace } from 'services/workspaceService';

import { getInitials } from 'utlis/globalFunctions';
import { errorReactToastify } from 'utlis/toasts';

import CreditUsageBar from './CreditUsageBar';

import styles from './styles.module.scss';

const BillingSection = ({ reviewsRemaining, totalCredits, creditPercentage }) => {
  const isPro = useIsPro();
  const { selectedWorkspace, isOwner } = useWorkspace();

  const [workspaceName, setWorkspaceName] = useState(selectedWorkspace?.name || '');
  const [logoPreview, setLogoPreview] = useState(selectedWorkspace?.avatar_url || null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);

  const debounceRef = useRef(null);
  const abortControllerRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setWorkspaceName(selectedWorkspace?.name || '');
    setLogoPreview(selectedWorkspace?.avatar_url || null);
  }, [selectedWorkspace?.uuid]);

  useEffect(() => () => {
    clearTimeout(debounceRef.current);
    abortControllerRef.current?.abort();
  }, []);

  const handleNameChange = e => {
    const value = e.target.value;

    setWorkspaceName(value);

    // Abort any in-flight request and cancel pending debounce
    clearTimeout(debounceRef.current);
    abortControllerRef.current?.abort();

    debounceRef.current = setTimeout(async () => {
      const trimmed = value.trim();

      if (!trimmed || trimmed === selectedWorkspace?.name) {
        return;
      }

      abortControllerRef.current = new AbortController();

      try {
        await updateWorkspace(selectedWorkspace.uuid, { name: trimmed }, abortControllerRef.current.signal);
        reloadWorkspaces();
      }
      catch (err) {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'AbortError') {
          return;
        }

        errorReactToastify(err?.message || 'Failed to update workspace name');
        setWorkspaceName(selectedWorkspace?.name || '');
      }
    }, 3000);
  };

  const handleLogoClick = () => {
    if (isOwner) {
      fileInputRef.current?.click();
    }
  };

  const handleRemoveLogo = async () => {
    setLogoPreview(null);

    try {
      await updateWorkspace(selectedWorkspace.uuid, { avatar_url: null });
      reloadWorkspaces();
    }
    catch (err) {
      errorReactToastify(err?.message || 'Failed to remove workspace logo');
      setLogoPreview(selectedWorkspace?.avatar_url || null);
    }
  };

  const handleLogoChange = async e => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    // Show local preview immediately
    const reader = new FileReader();

    reader.onload = ev => setLogoPreview(ev.target.result);
    reader.readAsDataURL(file);

    setIsUploadingLogo(true);

    try {
      const avatarUrl = await FilesService.uploadFileFromLocal(file);

      await updateWorkspace(selectedWorkspace.uuid, { avatar_url: avatarUrl });
      reloadWorkspaces();
    }
    catch (err) {
      errorReactToastify(err?.message || 'Failed to update workspace logo');
      setLogoPreview(selectedWorkspace?.avatar_url || null);
    }
    finally {
      setIsUploadingLogo(false);
      e.target.value = null;
    }
  };

  return (
    <div className={styles['billing-section']}>
      <div className={styles['section-heading']}>
        <span className={styles['heading-text']}>
          Workspace & Billing
        </span>
      </div>

      {/* Workspace name */}
      <div className={styles['workspace-name-row']}>
        <div className={cn('relative', { 'group': isOwner && !isUploadingLogo })}>
          <button
            type="button"
            className={cn(
              styles['workspace-icon-box'],
              isOwner ? 'cursor-pointer' : 'cursor-default',
              'group-hover:border-blue-500 transition-colors'
            )}
            onClick={handleLogoClick}
            disabled={isUploadingLogo || !isOwner}
            aria-label="Update workspace logo"
          >
            {isUploadingLogo
              ? <div className={styles['logo-uploading']} />
              : (logoPreview
                ? <img src={logoPreview} alt="" />
                : getInitials(selectedWorkspace?.name))}
          </button>

          {/* Remove logo — only shown when a logo exists */}
          {isOwner && logoPreview && (
            <button
              type="button"
              aria-label="Remove logo"
              className="absolute opacity-0 group-hover:opacity-100 transition-opacity
                flex items-center justify-center w-5 h-5 rounded-full
                bg-white border border-[#E1E4E8]"
              style={{ right: '-10px', top: '-10px' }}
              onClick={e => {
                e.stopPropagation();
                handleRemoveLogo();
              }}
            >
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2 2L10 10M10 2L2 10" stroke="#000000" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            </button>
          )}

          {/* Change avatar label */}
          {isOwner && (
            <button
              type="button"
              className="absolute opacity-0 group-hover:opacity-100 transition-opacity
                flex items-center justify-center w-[100px] h-6
                bg-white border border-[#E1E4E8] rounded-lg
                font-inter text-xs font-medium text-[#1C2531]
                left-1/2 -translate-x-1/2 whitespace-nowrap"
              style={{
                bottom: '-12px',
                boxShadow: '0px 2px 4px -1px rgba(0, 0, 0, 0.04), 0px 1px 1px rgba(0, 0, 0, 0.02)',
              }}
              onClick={handleLogoClick}
              tabIndex={-1}
            >
              Change avatar
            </button>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={handleLogoChange}
        />

        <div className={styles['workspace-name-field']}>
          <span className={styles['field-label']}>
            Workspace name
          </span>
          <input
            className={styles['field-input']}
            value={workspaceName}
            onChange={handleNameChange}
            readOnly={!isOwner}
          />
        </div>
      </div>

      {/* Active plan heading */}
      <div className={cn(styles['section-heading'], styles['plan-heading'])}>
        <span className={styles['heading-text']}>
          Active plan
        </span>
        <span>
          {isPro ? '(Pro tier)' : '(Free tier)'}
        </span>
      </div>

      <CreditUsageBar
        reviewsRemaining={reviewsRemaining}
        totalCredits={totalCredits}
        creditPercentage={creditPercentage}
      />
    </div>
  );
};

BillingSection.propTypes = {
  reviewsRemaining: PropTypes.number.isRequired,
  totalCredits: PropTypes.number.isRequired,
  creditPercentage: PropTypes.number.isRequired,
};

export default BillingSection;
